import { ArrowLeft, Flame } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  COMBUSTION_CORROSION_TOOL_IDENTITY,
  COMBUSTION_DATA_PROVENANCE,
  COMBUSTION_MODES,
  MODEL_BOUNDARIES,
  YEAR_6_RUST_CASES,
  type CombustionMode,
} from './combustionCorrosion';
import { ProcessEquationsMode } from './CombustionCorrosionEquations';
import { FireAndRustMode } from './CombustionCorrosionFire';
import { FuelBurnExplorer } from './FuelBurnExplorer';
import { RustInvestigation } from './CombustionCorrosionRust';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  CombustionMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  rusting: {
    tab: 'Year 6 · Rusting',
    eyebrow: 'Irreversible change',
    title: 'What does an iron nail need to rust?',
    description:
      'Look at each test tube, predict what the nail will look like after a week, then compare the tubes.',
  },
  'fire-and-rust': {
    tab: 'Year 8 · Fire & rust',
    eyebrow: 'Combustion and corrosion',
    title: 'Control fire and rust by controlling what they need',
    description:
      'Use the fire triangle, flame evidence and rusting conditions to predict what happens and why.',
  },
  equations: {
    tab: 'Year 9 · Equations',
    eyebrow: 'Word and balanced equations',
    title: 'Write burning, rusting, respiration and photosynthesis as equations',
    description:
      'Burn your own amount of fuel and check the oxygen, then set coefficients until every atom is conserved.',
  },
};

export function CombustionCorrosionLabPage() {
  const learnerYear = useChemistryToolYear();
  const [mode, setMode] = useState<CombustionMode>(() => modeForYear(COMBUSTION_MODES, learnerYear));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'combustion-corrosion', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-coral/25 bg-gradient-to-br from-wash-coral via-canvas-pure to-wash-sunshine p-6 shadow-card-soft lg:p-8"
        data-tool-key={COMBUSTION_CORROSION_TOOL_IDENTITY.toolKey}
        data-tool-version={COMBUSTION_CORROSION_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-coral">
              <Flame className="h-4 w-4" aria-hidden="true" /> Reactions with oxygen
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Combustion &amp; Corrosion Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Burning and rusting both use oxygen. Follow them from test-tube observations to the
              fire triangle and balanced equations.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 6–9 · progressive model
          </div>
        </div>

        <div className="mt-6 grid max-w-2xl grid-cols-3 rounded-full bg-white/75 p-1" aria-label="Choose learning level">
          {COMBUSTION_MODES.map(({ mode: option }) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-2 py-2.5 text-xs font-extrabold transition-colors motion-reduce:transition-none sm:px-3 sm:text-sm ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">{copy.eyebrow}</p>
        <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
        <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
      </section>

      {mode === 'rusting' ? (
        <RustInvestigation cases={YEAR_6_RUST_CASES} showConditionsBeforeReveal={false} />
      ) : null}
      {mode === 'fire-and-rust' ? <FireAndRustMode /> : null}
      {mode === 'equations' ? (
        <>
          <FuelBurnExplorer />
          <ProcessEquationsMode />
        </>
      ) : null}

      <section
        className="mt-6 rounded-3xl border border-brand-sunshine/40 bg-wash-sunshine p-5"
        aria-label="Model boundaries"
      >
        <p className="text-sm font-extrabold text-ink">Model boundaries and safety</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium leading-6 text-ink-soft">
          {MODEL_BOUNDARIES.map((boundary) => (
            <li key={boundary}>{boundary}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs font-bold leading-5 text-slate2">
          {COMBUSTION_DATA_PROVENANCE.conditions} Sources: {COMBUSTION_DATA_PROVENANCE.sources.join('; ')}.
          Cases are pending teaching review.
        </p>
      </section>

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases. Nothing is saved. It does not replace a
        supervised practical, act as fire-safety training, record an assessment attempt or claim
        alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
